import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Subject, of, takeUntil } from 'rxjs';
import { TagsService } from 'src/app/shared/api-services/tags.service';
import { UsersService } from 'src/app/shared/api-services/users.service';
import { VehiclesService } from 'src/app/shared/api-services/vehicles.service';
import { TasksService } from 'src/app/shared/api-services/tasks.service';
import { TenantService } from 'src/app/shared/api-services/tenant.service';
import { Tag } from 'src/app/shared/models/tag/tag';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { ToastrService } from 'ngx-toastr';
import { PatchTaskRequest } from 'src/app/shared/models/task/task-request';
import { TranslateService } from '@ngx-translate/core';
import { UserRequest } from 'src/app/shared/models/user/user-request';
import { VehicleRequest } from 'src/app/shared/models/vehicle/vehicle-request';
import { AssetsService } from 'src/app/shared/api-services/assets.service';
import { PatchAssetRequest } from 'src/app/shared/models/assets/asset-request';
import { InvoicesService } from 'src/app/shared/api-services/invoices.service';
import { SuppliersService } from 'src/app/shared/api-services/suppliers.service';
import { InvoiceRequest } from 'src/app/shared/models/invoice/invoice-request';
import { SupplierRequest } from 'src/app/shared/models/supplier/supplier-request';

export enum EntityType {
  USERS = 'users',
  VEHICLES = 'vehicles',
  ASSETS = 'assets',
  TASKS = 'tasks',
  INVOICES = 'invoices',
  SUPPLIERS = 'suppliers'
}

@Component({
  selector: 'tag-mass-association-modal',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TypeaheadModule
  ],
  templateUrl: './tag-mass-association-modal.component.html',
  standalone: true
})
export class TagMassAssociationModalComponent implements OnInit, OnDestroy {
  @Input() entityType: EntityType;
  @Input() entityIds: string[] = [];

  form: FormGroup;
  selectedTags: Tag[] = [];
  tags$: Observable<string[]> = of([]);
  
  tenantId: string;
  
  // Variabili per la barra di progresso
  onApplyProgressStep = false;
  progressBarValue = 0;
  elementProcessed = 0;
  totalElements = 0;
  
  private _destroy$ = new Subject<void>();

  constructor(
    private _tagsService: TagsService,
    private _usersService: UsersService,
    private _vehiclesService: VehiclesService,
    private _tasksService: TasksService,
    private _tenantService: TenantService,
    private _toastrService: ToastrService,
    private _assetsService: AssetsService,
    private _invoicesService: InvoicesService,
    private _suppliersService: SuppliersService,
    public bsModalRef: BsModalRef,
    private _translateService: TranslateService
  ) {
    this.form = new FormGroup({
      tag: new FormControl(null)
    });
    this.tenantId = this._tenantService.getTenantId();
  }

  ngOnInit(): void {
    this.tags$ = new Observable((observer) => {
      this._tagsService.listTags$({})
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (tags) => {
            observer.next(tags.items.map(tag => tag.name));
            observer.complete();
          }
        });
    });
  }

  onTagSelected(match: TypeaheadMatch): void {
    const tagName = match.value;

    this._tagsService.listTags$({ name: tagName })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (tags) => {
          if (tags.items.length > 0) {
            const newTag = tags.items[0];
            if (!this.selectedTags.some(tag => tag.id === newTag.id)) {
              this.selectedTags.push(newTag);
            }
            this.form.get('tag').reset();
          }
        }
      });
  }

  removeTag(tagId: string): void {
    this.selectedTags = this.selectedTags.filter(tag => tag.id !== tagId);
  }

  applyTags(): void {
    if (!this.selectedTags || this.selectedTags.length === 0) {
      this._toastrService.warning(this._translateService.instant('TAGS.SELECT_AT_LEAST_ONE_TAG'));
      return;
    }

    // Attiva la barra di progresso
    this.onApplyProgressStep = true;
    this.totalElements = this.entityIds.length;
    this.elementProcessed = 0;
    this.progressBarValue = 0;

    const tagIds = this.selectedTags.map(tag => tag.id);
    
    switch (this.entityType) {
      case EntityType.USERS:
        this.applyTagsToUsers(tagIds);
        break;
      case EntityType.VEHICLES:
        this.applyTagsToVehicles(tagIds);
        break;
      case EntityType.ASSETS:
        this.applyTagsToAssets(tagIds);
        break;
      case EntityType.TASKS:
        this.applyTagsToTasks(tagIds);
        break;
      case EntityType.INVOICES:
        this.applyTagsToInvoices(tagIds);
        break;
      case EntityType.SUPPLIERS:
        this.applyTagsToSuppliers(tagIds);
        break;
    }
  }

  private applyTagsToUsers(tagIds: string[]): void {
    this.entityIds.forEach(userId => {
      this._usersService.getUser$(this.tenantId, userId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (user) => {
            const userRequest: UserRequest = {
              ...user
            };

            if (!userRequest.tagIds) {
              userRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!userRequest.tagIds.includes(tagId)) {
                  userRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._usersService.updateUser$(this.tenantId, userId, userRequest, false)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }

  private applyTagsToVehicles(tagIds: string[]): void {
    this.entityIds.forEach(vehicleId => {
      this._vehiclesService.getVehicle$(this.tenantId, vehicleId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (vehicle) => {
            const vehicleRequest: VehicleRequest = this._vehiclesService.mapVehicleToRequest(vehicle);
            
            if (!vehicleRequest.tagIds) {
              vehicleRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!vehicleRequest.tagIds.includes(tagId)) {
                  vehicleRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._vehiclesService.updateVehicle$(this.tenantId, vehicleId, vehicleRequest, false)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }

  private applyTagsToAssets(tagIds: string[]): void {
    this.entityIds.forEach(assetId => {
      this._assetsService.getAsset$(this.tenantId, assetId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (asset) => {
            const assetRequest: PatchAssetRequest = {
              ...asset
            };
            
            if (!assetRequest.tagIds) {
              assetRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!assetRequest.tagIds.includes(tagId)) {
                  assetRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._assetsService.updateAsset$(this.tenantId, assetId, assetRequest, false)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }

  private applyTagsToTasks(tagIds: string[]): void {
    this.entityIds.forEach(taskId => {
      this._tasksService.getTask$(this.tenantId, taskId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (task) => {
            const taskRequest: PatchTaskRequest = {
              ...task,
              headquarterId: task.headquarterId,
              title: task.title
            };
            
            if (!taskRequest.tagIds) {
              taskRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!taskRequest.tagIds.includes(tagId)) {
                  taskRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._tasksService.patchTask$(this.tenantId, taskId, taskRequest, false)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }

  private applyTagsToInvoices(tagIds: string[]): void {
    this.entityIds.forEach(invoiceId => {
      this._invoicesService.getInvoice$(this.tenantId, invoiceId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (invoice) => {
            const invoiceRequest: InvoiceRequest = {
              ...invoice
            };
            
            if (!invoiceRequest.tagIds) {
              invoiceRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!invoiceRequest.tagIds.includes(tagId)) {
                  invoiceRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._invoicesService.updateInvoice$(this.tenantId, invoiceId, invoiceRequest)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.INVOICE_UPDATED_ERROR'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }
  
  private applyTagsToSuppliers(tagIds: string[]): void {
    this.entityIds.forEach(supplierId => {
      this._suppliersService.getSupplier$(this.tenantId, supplierId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (supplier) => {
            const supplierRequest: SupplierRequest = {
              name: supplier.name,
              code: supplier.code,
              vatNumber: supplier.vatNumber,
              extraFields: supplier.extraFields
            };
            
            if (!supplierRequest.tagIds) {
              supplierRequest.tagIds = [...tagIds];
            } else {
              tagIds.forEach(tagId => {
                if (!supplierRequest.tagIds.includes(tagId)) {
                  supplierRequest.tagIds.push(tagId);
                }
              });
            }
            
            this._suppliersService.updateSupplier$(this.tenantId, supplierId, supplierRequest)
              .pipe(takeUntil(this._destroy$))
              .subscribe({
                next: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                },
                error: () => {
                  this.elementProcessed++;
                  this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
                  
                  if (this.elementProcessed === this.totalElements) {
                    this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
                    this.bsModalRef.hide();
                  }
                }
              });
          },
          error: () => {
            this.elementProcessed++;
            this.progressBarValue = (this.elementProcessed / this.totalElements) * 100;
            
            if (this.elementProcessed === this.totalElements) {
              this._toastrService.success(this._translateService.instant('TAGS.SUCCESS_ASSOCIATION'));
              this.bsModalRef.hide();
            }
          }
        });
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
} 